import { FormEvent, useMemo, useState } from "react";
import { Chip } from "@/components/primitives";

type Cat = "Clothing" | "Shoes" | "Bags" | "Accessories" | "Jewellery";

interface Props {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void> | void;
}

const CATEGORIES: Cat[] = ["Clothing", "Shoes", "Bags", "Accessories", "Jewellery"];

const ERAS = ["1920s","1930s","1940s","1950s","1960s","1970s","1980s","1990s","Any"];
const CONDITIONS = ["Excellent","Good","Fair","Any"];
const CLOTH_SIZES = ["XS","S","M","L","XL","XXL"];
const LENGTHS = ["Mini","Midi","Maxi","Cropped","Any"];
const SHOE_SYSTEMS = ["US","EU","UK"];
const SHOE_WIDTHS = ["Narrow","Regular","Wide","Any"];
const SHOE_STYLES = ["Heels","Flats","Boots","Loafers","Sneakers","Sandals","Mules","Any"];
const BAG_STYLES = ["Tote","Shoulder","Crossbody","Clutch","Satchel","Backpack","Any"];
const BAG_SIZES = ["Small","Medium","Large","Any"];
const HARDWARE = ["Gold","Silver","Brass","Any"];
const MATERIALS = ["Leather","Suede","Canvas","Fabric","Any"];
const ACCESS_TYPES = ["Belt","Scarf","Hat","Gloves","Sunglasses","Hair accessory","Brooch","Any"];
const HAT_SIZES = ["S/M","L/XL","One size"];
const FRAME_SIZES = ["Small","Medium","Large","Oversized"];
const JEW_TYPES = ["Ring","Necklace","Earrings","Bracelet","Brooch","Any"];
const METALS = ["Gold","Silver","Rose gold","Brass","Any"];
const NECK_LENGTHS = ["Choker","Princess (18\")","Matinee (22\")","Opera (30\"+)","Any"];
const EAR_STYLES = ["Studs","Hoops","Drop","Chandelier","Clip-on","Any"];

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px",
  border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))",
  fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "hsl(var(--ink))", outline: "none",
};

function PillRow({ options, value, onChange, multi }: {
  options: string[]; value: string | string[] | null;
  onChange: (v: string | string[]) => void; multi?: boolean;
}) {
  const isActive = (o: string) => multi
    ? Array.isArray(value) && value.includes(o)
    : value === o;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((o) => {
        const active = isActive(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => {
              if (multi) {
                const arr = Array.isArray(value) ? [...value] : [];
                const idx = arr.indexOf(o);
                if (idx >= 0) arr.splice(idx, 1); else arr.push(o);
                onChange(arr);
              } else {
                onChange(active ? "" : o);
              }
            }}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
              padding: "5px 9px",
              border: "1px solid hsl(var(--ink))",
              background: active ? "hsl(var(--ink))" : "transparent",
              color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
              cursor: "pointer",
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="caps" style={{ display: "block", marginBottom: 6 }}>{children}</span>;
}

function Field({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ animation: "fig-fadeup 240ms ease both" }}>
      {children}
    </div>
  );
}

export function GrailForm({ busy, onCancel, onSubmit }: Props) {
  const [category, setCategory] = useState<Cat | "">("");
  const [item, setItem] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  // Clothing
  const [clothSize, setClothSize] = useState("");
  const [vintage, setVintage] = useState(false);
  const [waist, setWaist] = useState("");
  const [bust, setBust] = useState("");
  const [length, setLength] = useState("");
  // Shared era/condition
  const [eras, setEras] = useState<string[]>([]);
  const [condition, setCondition] = useState("");
  // Shoes
  const [sizeSystem, setSizeSystem] = useState("US");
  const [sizeValue, setSizeValue] = useState("");
  const [sizeWidth, setSizeWidth] = useState("");
  const [shoeStyles, setShoeStyles] = useState<string[]>([]);
  // Bags
  const [bagStyles, setBagStyles] = useState<string[]>([]);
  const [bagSize, setBagSize] = useState("");
  const [hardware, setHardware] = useState("");
  const [materials, setMaterials] = useState<string[]>([]);
  // Accessories
  const [accessTypes, setAccessTypes] = useState<string[]>([]);
  const [waistBelt, setWaistBelt] = useState("");
  const [hatSize, setHatSize] = useState("");
  const [frameSize, setFrameSize] = useState("");
  // Jewellery
  const [jewTypes, setJewTypes] = useState<string[]>([]);
  const [ringSize, setRingSize] = useState("");
  const [necklaceLen, setNecklaceLen] = useState("");
  const [earStyle, setEarStyle] = useState("");
  const [wristSize, setWristSize] = useState("");
  const [metal, setMetal] = useState("");

  const clearSizes = () => {
    setClothSize(""); setVintage(false); setWaist(""); setBust(""); setLength("");
    setSizeValue(""); setSizeWidth("");
    setHatSize(""); setFrameSize(""); setWaistBelt("");
    setRingSize(""); setNecklaceLen(""); setEarStyle(""); setWristSize("");
  };

  const placeholder = useMemo(() => {
    if (sizeSystem === "EU") return "e.g. 38, 39, 40";
    if (sizeSystem === "UK") return "e.g. 6, 6.5, 7";
    return "e.g. 8, 8.5, 9";
  }, [sizeSystem]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!item.trim() || !category) return;
    const payload: Record<string, unknown> = {
      item: item.trim(),
      max_price: maxPrice ? Number(maxPrice) : undefined,
      category,
      era_preference: eras.length ? eras : undefined,
      condition_preference: condition || undefined,
    };
    if (category === "Clothing") {
      Object.assign(payload, {
        size: clothSize || undefined,
        size_value: clothSize || undefined,
        vintage_sizing: vintage,
        waist_inches: waist || undefined,
        bust_chest_inches: bust || undefined,
        length_preference: length || undefined,
      });
    } else if (category === "Shoes") {
      Object.assign(payload, {
        size_system: sizeSystem,
        size_value: sizeValue || undefined,
        size: sizeValue ? `${sizeSystem} ${sizeValue}` : undefined,
        size_width: sizeWidth || undefined,
        style_preference: shoeStyles.length ? shoeStyles : undefined,
      });
    } else if (category === "Bags") {
      Object.assign(payload, {
        style_preference: bagStyles.length ? bagStyles : undefined,
        size_value: bagSize || undefined,
        size: bagSize || undefined,
        hardware_preference: hardware || undefined,
        material_preference: materials.length ? materials : undefined,
      });
    } else if (category === "Accessories") {
      Object.assign(payload, {
        accessory_type: accessTypes.length ? accessTypes : undefined,
        waist_inches: accessTypes.includes("Belt") && waistBelt ? waistBelt : undefined,
        hat_size: accessTypes.includes("Hat") && hatSize ? hatSize : undefined,
        frame_size: accessTypes.includes("Sunglasses") && frameSize ? frameSize : undefined,
      });
    } else if (category === "Jewellery") {
      Object.assign(payload, {
        jewellery_type: jewTypes.length ? jewTypes : undefined,
        ring_size: jewTypes.includes("Ring") && ringSize ? ringSize : undefined,
        necklace_length: jewTypes.includes("Necklace") && necklaceLen ? necklaceLen : undefined,
        earring_style: jewTypes.includes("Earrings") && earStyle ? earStyle : undefined,
        wrist_size: jewTypes.includes("Bracelet") && wristSize ? wristSize : undefined,
        metal_preference: metal || undefined,
      });
    }
    await onSubmit(payload);
  };

  return (
    <form onSubmit={submit} style={{
      background: "hsl(var(--cream))", border: "1.5px solid hsl(var(--ink))",
      padding: "14px", display: "flex", flexDirection: "column", gap: 14,
    }}>
      {/* Category */}
      <div>
        <FieldLabel>Category</FieldLabel>
        <PillRow
          options={CATEGORIES as unknown as string[]}
          value={category}
          onChange={(v) => {
            const next = (v as Cat) || "";
            if (next !== category) clearSizes();
            setCategory(next);
          }}
        />
      </div>

      {/* Dynamic fields */}
      {category === "Clothing" && (
        <Field>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <FieldLabel>Size</FieldLabel>
              <PillRow options={CLOTH_SIZES} value={clothSize} onChange={(v) => setClothSize(v as string)} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink))" }}>
              <input type="checkbox" checked={vintage} onChange={(e) => setVintage(e.target.checked)} />
              Vintage sizing (runs small)
            </label>
            {vintage && (
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))" }}>
                Vintage pieces often run 1–2 sizes smaller than modern sizing.
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>Waist (in)</FieldLabel>
                <input value={waist} onChange={(e) => setWaist(e.target.value)} placeholder="optional" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Bust/Chest (in)</FieldLabel>
                <input value={bust} onChange={(e) => setBust(e.target.value)} placeholder="optional" style={inputStyle} />
              </div>
            </div>
            <div>
              <FieldLabel>Length</FieldLabel>
              <PillRow options={LENGTHS} value={length} onChange={(v) => setLength(v as string)} />
            </div>
          </div>
        </Field>
      )}

      {category === "Shoes" && (
        <Field>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <FieldLabel>Size system</FieldLabel>
              <PillRow options={SHOE_SYSTEMS} value={sizeSystem} onChange={(v) => setSizeSystem(v as string)} />
            </div>
            <div>
              <FieldLabel>Size</FieldLabel>
              <input value={sizeValue} onChange={(e) => setSizeValue(e.target.value)} placeholder={placeholder} style={inputStyle} />
            </div>
            <div>
              <FieldLabel>Width</FieldLabel>
              <PillRow options={SHOE_WIDTHS} value={sizeWidth} onChange={(v) => setSizeWidth(v as string)} />
            </div>
            <div>
              <FieldLabel>Style</FieldLabel>
              <PillRow multi options={SHOE_STYLES} value={shoeStyles} onChange={(v) => setShoeStyles(v as string[])} />
            </div>
          </div>
        </Field>
      )}

      {category === "Bags" && (
        <Field>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <FieldLabel>Style</FieldLabel>
              <PillRow multi options={BAG_STYLES} value={bagStyles} onChange={(v) => setBagStyles(v as string[])} />
            </div>
            <div>
              <FieldLabel>Size</FieldLabel>
              <PillRow options={BAG_SIZES} value={bagSize} onChange={(v) => setBagSize(v as string)} />
            </div>
            <div>
              <FieldLabel>Hardware</FieldLabel>
              <PillRow options={HARDWARE} value={hardware} onChange={(v) => setHardware(v as string)} />
            </div>
            <div>
              <FieldLabel>Material</FieldLabel>
              <PillRow multi options={MATERIALS} value={materials} onChange={(v) => setMaterials(v as string[])} />
            </div>
          </div>
        </Field>
      )}

      {category === "Accessories" && (
        <Field>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <FieldLabel>Type</FieldLabel>
              <PillRow multi options={ACCESS_TYPES} value={accessTypes} onChange={(v) => setAccessTypes(v as string[])} />
            </div>
            {accessTypes.includes("Belt") && (
              <Field>
                <FieldLabel>Waist size (in)</FieldLabel>
                <input value={waistBelt} onChange={(e) => setWaistBelt(e.target.value)} placeholder="e.g. 28, 30, 32" style={inputStyle} />
              </Field>
            )}
            {accessTypes.includes("Hat") && (
              <Field>
                <FieldLabel>Hat size</FieldLabel>
                <PillRow options={HAT_SIZES} value={hatSize} onChange={(v) => setHatSize(v as string)} />
              </Field>
            )}
            {accessTypes.includes("Sunglasses") && (
              <Field>
                <FieldLabel>Frame size</FieldLabel>
                <PillRow options={FRAME_SIZES} value={frameSize} onChange={(v) => setFrameSize(v as string)} />
              </Field>
            )}
          </div>
        </Field>
      )}

      {category === "Jewellery" && (
        <Field>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <FieldLabel>Type</FieldLabel>
              <PillRow multi options={JEW_TYPES} value={jewTypes} onChange={(v) => setJewTypes(v as string[])} />
            </div>
            {jewTypes.includes("Ring") && (
              <Field>
                <FieldLabel>Ring size</FieldLabel>
                <input value={ringSize} onChange={(e) => setRingSize(e.target.value)} placeholder="e.g. 6, 7, 8 (US sizing)" style={inputStyle} />
              </Field>
            )}
            {jewTypes.includes("Necklace") && (
              <Field>
                <FieldLabel>Length</FieldLabel>
                <PillRow options={NECK_LENGTHS} value={necklaceLen} onChange={(v) => setNecklaceLen(v as string)} />
              </Field>
            )}
            {jewTypes.includes("Earrings") && (
              <Field>
                <FieldLabel>Earring style</FieldLabel>
                <PillRow options={EAR_STYLES} value={earStyle} onChange={(v) => setEarStyle(v as string)} />
              </Field>
            )}
            {jewTypes.includes("Bracelet") && (
              <Field>
                <FieldLabel>Wrist size</FieldLabel>
                <input value={wristSize} onChange={(e) => setWristSize(e.target.value)} placeholder='e.g. 6.5" (measure loosely)' style={inputStyle} />
              </Field>
            )}
            {(jewTypes.includes("Ring") || jewTypes.includes("Necklace") || jewTypes.includes("Earrings") || jewTypes.includes("Bracelet")) && (
              <Field>
                <FieldLabel>Metal</FieldLabel>
                <PillRow options={METALS} value={metal} onChange={(v) => setMetal(v as string)} />
              </Field>
            )}
          </div>
        </Field>
      )}

      {/* Era + Condition (shared, all categories) */}
      {category && (
        <Field>
          <FieldLabel>Era preference</FieldLabel>
          <PillRow multi options={ERAS} value={eras} onChange={(v) => setEras(v as string[])} />
        </Field>
      )}
      {category && (
        <Field>
          <FieldLabel>Condition</FieldLabel>
          <PillRow options={CONDITIONS} value={condition} onChange={(v) => setCondition(v as string)} />
        </Field>
      )}

      {/* All-category fields below */}
      <div>
        <FieldLabel>Item description / keywords</FieldLabel>
        <textarea
          required
          value={item}
          onChange={(e) => setItem(e.target.value)}
          placeholder="70s Pendleton chore coat"
          rows={2}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </div>
      <div>
        <FieldLabel>Max price ($)</FieldLabel>
        <input type="number" min="0" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="140" style={inputStyle} />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button type="button" onClick={onCancel} className="btn" style={{ flex: 1, padding: "10px" }}>Cancel</button>
        <button type="submit" disabled={busy || !category || !item.trim()} className="btn btn-primary" style={{ flex: 2, padding: "10px" }}>
          {busy ? "…" : "Save grail"}
        </button>
      </div>
    </form>
  );
}
